import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { isElectron, electronAPI } from '../utils/electronBridge';

/**
 * UpdateBanner — In-app notification for desktop auto-updates.
 *
 * Listens to Electron updater IPC events and shows a slim banner at the top
 * of the app when an update is available, downloading, or ready to install.
 *
 * Only renders in Electron — returns null in the browser.
 */

const STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  AVAILABLE: 'available',
  DOWNLOADING: 'downloading',
  DOWNLOADED: 'downloaded',
  UP_TO_DATE: 'up-to-date',
  ERROR: 'error',
};

/** Validate backend status string; returns IDLE for unknown values. */
const mapStatus = (s) => {
  switch (s) {
    case STATUS.CHECKING: return STATUS.CHECKING;
    case STATUS.AVAILABLE: return STATUS.AVAILABLE;
    case STATUS.DOWNLOADING: return STATUS.DOWNLOADING;
    case STATUS.UP_TO_DATE: return STATUS.UP_TO_DATE;
    case STATUS.DOWNLOADED: return STATUS.DOWNLOADED;
    case STATUS.ERROR: return STATUS.ERROR;
    default: return STATUS.IDLE;
  }
};

/** Monochrome color palette — black and white only. */
const getColors = (isDarkMode) =>
  isDarkMode
    ? {
        bg: '#111111',
        border: 'rgba(255,255,255,0.1)',
        text: '#e0e0e0',
        textMuted: '#888888',
        accent: '#ffffff',
        accentHover: '#cccccc',
        progressBg: '#222222',
        progressFill: '#888888',
        dismiss: '#666666',
      }
    : {
        bg: '#f5f5f5',
        border: 'rgba(0,0,0,0.1)',
        text: '#1a1a1a',
        textMuted: '#666666',
        accent: '#000000',
        accentHover: '#333333',
        progressBg: '#e0e0e0',
        progressFill: '#555555',
        dismiss: '#999999',
      };

/** Shared banner row layout with dismiss button. */
const BannerRow = ({ colors, icon, children, onDismiss }) => (
  <div
    role="status"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 16px',
      fontSize: '13px',
      fontFamily: "'Inter', sans-serif",
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.bg,
      color: colors.text,
      zIndex: 9999,
      flexShrink: 0,
    }}
  >
    {icon}
    {children}
    <button
      onClick={onDismiss}
      title="Dismiss"
      aria-label="Dismiss update notification"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px',
        color: colors.dismiss,
        marginLeft: 'auto',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <X size={14} />
    </button>
  </div>
);

const UpdateBanner = ({ isDarkMode }) => {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [version, setVersion] = useState(null);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const colors = useMemo(() => getColors(isDarkMode), [isDarkMode]);

  const buttonBase = useMemo(
    () => ({
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: 500,
      fontFamily: 'inherit',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      color: isDarkMode ? '#000000' : '#ffffff',
      backgroundColor: colors.accent,
      transition: 'background-color 0.15s, opacity 0.15s',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }),
    [isDarkMode, colors.accent],
  );

  // Subscribe to updater events from Electron main process
  useEffect(() => {
    if (!isElectron) return;

    const cleanupStatus = electronAPI.updater.onStatus((data) => {
      const mapped = mapStatus(data.status);
      setStatus(mapped);

      if (mapped === STATUS.AVAILABLE || mapped === STATUS.DOWNLOADED) {
        setVersion(data.version || null);
        setDownloading(false);
        // Only un-dismiss for actionable states — avoids banner reappearing for
        // the same version after a periodic re-check fires 'checking' then 'available'.
        setDismissed(false);
      }
      if (mapped === STATUS.ERROR) {
        setError(data.error || 'Update check failed');
        setDownloading(false);
        setDismissed(false);
      }
    });

    const cleanupProgress = electronAPI.updater.onProgress((data) => {
      setStatus(STATUS.DOWNLOADING);
      setPercent(data.percent || 0);
      setDownloading(true);
    });

    return () => {
      cleanupStatus();
      cleanupProgress();
    };
  }, []);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const result = await electronAPI.updater.download();
      if (result && !result.success) {
        setStatus(STATUS.ERROR);
        setError(result.error || 'Download failed');
        setDownloading(false);
      }
    } catch (err) {
      setStatus(STATUS.ERROR);
      setError(err.message || 'Download failed');
      setDownloading(false);
    }
  }, []);

  const handleInstall = useCallback(() => {
    electronAPI.updater.install();
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Don't render in browser or when dismissed
  if (!isElectron || dismissed) return null;

  // Only show for actionable states
  if (
    status === STATUS.IDLE ||
    status === STATUS.CHECKING ||
    status === STATUS.UP_TO_DATE
  ) {
    return null;
  }

  if (status === STATUS.AVAILABLE) {
    return (
      <BannerRow
        colors={colors}
        icon={<Download size={14} color={colors.text} />}
        onDismiss={handleDismiss}
      >
        <span>
          Version <strong>{version}</strong> is available.
        </span>
        <button
          style={{
            ...buttonBase,
            opacity: downloading ? 0.6 : 1,
            cursor: downloading ? 'default' : 'pointer',
          }}
          onClick={downloading ? undefined : handleDownload}
          disabled={downloading}
          aria-label={downloading ? 'Download starting' : 'Download update'}
          onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = colors.accentHover; }}
          onMouseLeave={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = colors.accent; }}
        >
          {downloading ? 'Starting...' : <><Download size={11} /> Download</>}
        </button>
      </BannerRow>
    );
  }

  if (status === STATUS.DOWNLOADING) {
    return (
      <BannerRow
        colors={colors}
        icon={<Download size={14} color={colors.text} />}
        onDismiss={handleDismiss}
      >
        <span>Downloading update... {Math.round(percent)}%</span>
        <div
          role="progressbar"
          aria-valuenow={Math.round(percent)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Download progress"
          style={{
            flex: 1,
            maxWidth: '200px',
            height: '6px',
            borderRadius: '3px',
            backgroundColor: colors.progressBg,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: '100%',
              borderRadius: '3px',
              backgroundColor: colors.progressFill,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </BannerRow>
    );
  }

  if (status === STATUS.DOWNLOADED) {
    return (
      <BannerRow
        colors={colors}
        icon={<CheckCircle size={14} color={colors.text} />}
        onDismiss={handleDismiss}
      >
        <span>
          Version <strong>{version}</strong> is ready. Restart to install.
        </span>
        <button
          style={buttonBase}
          onClick={handleInstall}
          aria-label="Restart to install update"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.accentHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.accent; }}
        >
          Restart Now
        </button>
      </BannerRow>
    );
  }

  if (status === STATUS.ERROR) {
    return (
      <BannerRow
        colors={colors}
        icon={<AlertTriangle size={14} color={colors.textMuted} />}
        onDismiss={handleDismiss}
      >
        <span style={{ color: colors.textMuted }}>
          Update check failed: {error}
        </span>
      </BannerRow>
    );
  }

  return null;
};

export default UpdateBanner;
